import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import ImmutablePropTypes from 'react-immutable-proptypes';
import classNames from 'classnames';
import { OverlayTrigger, Tooltip } from '@saucelabs/sl-component-overlay';
import { actions as usageActions, selectors } from '@saucelabs/sl-module-usage';
import { numberHelpers } from '@saucelabs/sl-util-helpers';
import { Typography } from '@saucelabs/ds-typography';
import { Link } from '@saucelabs/ds-link';
import { Units } from '@saucelabs/sl-module-usage/lib/constants';

import DCExplanationTooltip from '../components/DCExplanationTooltip';
import { UsageSessionChart } from '../components/Charts';

const UsageBillingUsageContainer = ({
  sidebarCollapsed,
  startUsageUnitsPoller,
  stopUsageUnitsPoller,
  usageUnits,
  user,
  userConcurrency,
}) => {
  useEffect(() => {
    startUsageUnitsPoller();
    return stopUsageUnitsPoller;
  }, []);

  return (
    <>
      <RemainingUsageUnits
        sidebarCollapsed={sidebarCollapsed}
        usageUnits={usageUnits}
      />
      <MyTeamSessions
        sidebarCollapsed={sidebarCollapsed}
        userConcurrency={userConcurrency}
      />
      <OrganizationSessions
        sidebarCollapsed={sidebarCollapsed}
        usageUnit={usageUnits?.unit}
        user={user}
        userConcurrency={userConcurrency}
      />
    </>
  );
};

const RemainingUsageUnits = ({
  sidebarCollapsed,
  usageUnits,
}) => {
  if (usageUnits?.unit === undefined) {
    return null;
  }

  const unitsNumber = Math.max(
    usageUnits.unit === Units.Credits ? usageUnits.number : Math.floor(usageUnits.number / 60),
    0,
  );
  const unitName = usageUnits.unit === Units.Credits ? usageUnits.unit : 'minutes';

  return (
    <>
      {Number.isFinite(unitsNumber) && (
        <div className="mb-1.5">
          {sidebarCollapsed ? (
            <OverlayTrigger
              delay={150}
              overlay={(
                <Tooltip id="usage-credits-sidebar-tooltip">
                  <span className="font-normal font-semibold capitalize">
                    {unitName}
                    {' Remaining: '}
                    {numberHelpers.formatThousand(unitsNumber, 'standard')}
                  </span>
                </Tooltip>
              )}
              placement="right"
              rootClose
            >
              <div className="cursor-pointer">
                <Typography
                  className="block text-gray-600 text-center font-normal capitalize"
                  component="span"
                  disableSpacing
                  variant="body-small"
                >
                  {unitName}
                </Typography>
                {unitsNumber < 100000 ? (
                  <Typography
                    className="block text-gray-900 text-center font-semibold"
                    component="span"
                    disableSpacing
                    variant="body"
                  >
                    {numberHelpers.formatThousand(unitsNumber, 'standard')}
                  </Typography>
                ) : (
                  <Typography
                    className="block text-gray-900 text-center font-semibold"
                    component="span"
                    disableSpacing
                    variant="body"
                  >
                    {numberHelpers.formatThousand(unitsNumber, 'compact')}
                  </Typography>
                )}
              </div>
            </OverlayTrigger>
          ) : (
            <div className="mb-3 px-2">
              <Typography
                className="text-gray-900"
                component="span"
                disableSpacing
                variant="heading-5"
              >
                {numberHelpers.formatThousand(unitsNumber, 'standard')}
              </Typography>
              <Typography
                className="text-gray-900 font-normal"
                component="span"
                disableSpacing
                variant="body-small"
              >
                {' '}
                {unitName}
                {' remaining'}
              </Typography>
            </div>
          )}
        </div>
      )}
    </>
  );
};

const MyTeamSessions = ({
  sidebarCollapsed,
  userConcurrency,
}) => {
  const showRdcCharts = userConcurrency.organization?.get('allowed').rdc > 0;
  const showVdcCharts = userConcurrency.organization?.get('allowed').overall > 0;
  const showTeamCharts = userConcurrency.team?.get('allowed');
  const entitlementsGranted = showRdcCharts || showVdcCharts;

  const TeamSessionsRealLabel = () => (
    <div className="text-left">
      My Team&apos;s Sessions
      <br />
      {'Public Real Devices: '}
      {userConcurrency.team.get('current').rdc}
      /
      {userConcurrency.team.get('allowed').rdc}
    </div>
  );
  const TeamSessionsVirtualLabel = () => (
    <div className="text-left">
      My Team&apos;s Sessions
      <br />
      {'Virtual Devices: '}
      {userConcurrency.team.get('current').overall}
      /
      {userConcurrency.team.get('allowed').overall}
    </div>
  );

  return (
    <div className="mb-3 px-2">
      {entitlementsGranted && showTeamCharts && !sidebarCollapsed && (
        <Typography
          className="mb-0.5"
          variant="overline"
        >
          My Team&apos;s Sessions
        </Typography>
      )}
      {showTeamCharts && sidebarCollapsed && (
        <Typography
          className="flex justify-center"
          variant="overline"
        >
          team
        </Typography>
      )}
      {showTeamCharts && showRdcCharts && (
        <UsageSessionChart
          collapsed={sidebarCollapsed}
          collapsedChartLabel="real"
          current={userConcurrency.team.get('current').rdc}
          label="Public Real Devices"
          limit={userConcurrency.team.get('allowed').rdc}
          tooltipLabel={<TeamSessionsRealLabel />}
        />
      )}
      {showTeamCharts && showVdcCharts && (
        <UsageSessionChart
          collapsed={sidebarCollapsed}
          collapsedChartLabel="virtual"
          current={userConcurrency.team.get('current').overall}
          label="Virtual Devices"
          limit={userConcurrency.team.get('allowed').overall}
          tooltipLabel={<TeamSessionsVirtualLabel />}
        />
      )}
    </div>
  );
};

const OrganizationSessions = ({
  sidebarCollapsed,
  usageUnit,
  user,
  userConcurrency,
}) => {
  const showRdcCharts = userConcurrency.organization?.get('allowed').rdc > 0;
  const showVdcCharts = userConcurrency.organization?.get('allowed').overall > 0;
  const entitlementsGranted = showRdcCharts || showVdcCharts;

  const OrgSessionsRealLabel = () => (
    <div className="text-left">
      Organization Sessions
      <br />
      {'Public Real Devices: '}
      {userConcurrency.organization.get('current').rdc}
      /
      {userConcurrency.organization.get('allowed').rdc}
    </div>
  );
  const OrgSessionsVirtualLabel = () => (
    <div className="text-left">
      Organization Sessions
      <br />
      {'Virtual Devices: '}
      {userConcurrency.organization.get('current').overall}
      /
      {userConcurrency.organization.get('allowed').overall}
    </div>
  );

  return (
    <div className="mb-3 px-2">
      {entitlementsGranted && !sidebarCollapsed && (
        <Typography
          className="flex mb-0.5"
          variant="overline"
        >
          <div className="flex flex-row">
            <span className="mr-1">Organization Sessions</span>
            <DCExplanationTooltip />
          </div>
        </Typography>
      )}
      {sidebarCollapsed && (
        <Typography
          className="flex justify-center"
          variant="overline"
        >
          org
        </Typography>
      )}
      {showRdcCharts && (
        <UsageSessionChart
          collapsed={sidebarCollapsed}
          collapsedChartLabel="real"
          current={userConcurrency.organization.get('current').rdc}
          label="Public Real Devices"
          limit={userConcurrency.organization.get('allowed').rdc}
          tooltipLabel={<OrgSessionsRealLabel />}
        />
      )}
      {showVdcCharts && (
        <UsageSessionChart
          collapsed={sidebarCollapsed}
          collapsedChartLabel="virtual"
          current={userConcurrency.organization.get('current').overall}
          label="Virtual Devices"
          limit={userConcurrency.organization.get('allowed').overall}
          tooltipLabel={<OrgSessionsVirtualLabel />}
        />
      )}
      {user?.isOrgAdmin() && usageUnit === Units.Credits && (
        <div className={classNames('mt-2 flex', { 'text-center': sidebarCollapsed })}>
          <Link
            emphasize
            size={sidebarCollapsed ? 'default' : 'small'}
            to="/billing/usage-details"
          >
            {`View ${sidebarCollapsed ? '' : 'Usage'} Details`}
          </Link>
        </div>
      )}
    </div>
  );
};

UsageBillingUsageContainer.defaultProps = {
  usageUnits: null,
};

UsageBillingUsageContainer.propTypes = {
  sidebarCollapsed: PropTypes.bool.isRequired,
  startUsageUnitsPoller: PropTypes.func.isRequired,
  stopUsageUnitsPoller: PropTypes.func.isRequired,
  usageUnits: PropTypes.shape({
    number: PropTypes.number,
    unit: Units,
  }),
  user: ImmutablePropTypes.record.isRequired,
  userConcurrency: PropTypes.shape({
    organization: ImmutablePropTypes.map.isRequired,
    team: ImmutablePropTypes.map,
  }).isRequired,
};

RemainingUsageUnits.defaultProps = {
  usageUnits: null,
};

RemainingUsageUnits.propTypes = {
  sidebarCollapsed: PropTypes.bool.isRequired,
  usageUnits: PropTypes.shape({
    number: PropTypes.number,
    unit: Units,
  }),
};

MyTeamSessions.propTypes = {
  sidebarCollapsed: PropTypes.bool.isRequired,
  userConcurrency: PropTypes.shape({
    organization: ImmutablePropTypes.map.isRequired,
    team: ImmutablePropTypes.map,
  }).isRequired,
};

OrganizationSessions.defaultProps = {
  usageUnit: null,
};

OrganizationSessions.propTypes = {
  sidebarCollapsed: PropTypes.bool.isRequired,
  usageUnit: Units,
  user: ImmutablePropTypes.record.isRequired,
  userConcurrency: PropTypes.shape({
    organization: ImmutablePropTypes.map.isRequired,
    team: ImmutablePropTypes.map,
  }).isRequired,
};

const mapStateToProps = selectors.usageSelector;

const mapDispatchToProps = {
  startUsageUnitsPoller: usageActions.subscribeUsage,
  stopUsageUnitsPoller: usageActions.unsubscribeUsage,
};

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps,
)(UsageBillingUsageContainer));
export {
  UsageBillingUsageContainer,
  RemainingUsageUnits,
  MyTeamSessions,
  OrganizationSessions,
  mapStateToProps,
  mapDispatchToProps,
};
